import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  FileText,
  Calendar,
  StickyNote,
  Mail,
  MailCheck,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  User,
  Loader2,
  ExternalLink,
  Send,
  TrendingUp,
  UserCheck,
  Building2,
} from 'lucide-react';
import { EmailPreviewModal } from './EmailPreviewModal';
import { MeetingReportView } from '../meeting/MeetingReportView';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { edgeErrorMessage } from '../../lib/edgeError';
import { useSidebar } from '../../contexts/SidebarContext';
import { useMeetingReportSendState } from '../../hooks/useMeetingReportSendState';
import { sendReasonText } from '../../lib/deliverables/meetingReportSend';
import type { Json, Tables } from '../../types/supabase';
import type {
  MeetingReport,
  MeetingReportStatus,
  ExtractedAction,
} from '../../types/meetingReport';
import {
  EXTRACTED_ACTION_TYPE_LABELS,
  EXTRACTED_ACTION_TYPE_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
} from '../../types/meetingReport';

interface MeetingReportSidebarProps {
  reportId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_CONFIG: Record<MeetingReportStatus, { label: string; color: string; icon: typeof FileText }> = {
  draft: { label: 'Brouillon', color: 'bg-neutral-100 text-neutral-700', icon: FileText },
  transcript_fetched: { label: 'Transcript recupere', color: 'bg-blue-50 text-[#0066FF]', icon: FileText },
  cr_generated: { label: 'CR genere', color: 'bg-purple-100 text-purple-700', icon: CheckCircle2 },
  actions_validated: { label: 'Actions validees', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  email_sent: { label: 'Email envoye', color: 'bg-emerald-100 text-emerald-700', icon: MailCheck },
  error: { label: 'Erreur', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  no_meeting: { label: 'Reunion non verifiee', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
};

interface RelatedAppointment {
  id: string;
  title: string;
  start_time: string;
  contact_id: string | null;
  account_id: string | null;
  opportunity_id: string | null;
  onboarding_lead_id: string | null;
  contact?: { id: string; first_name: string; last_name: string; email?: string } | null;
  account?: { id: string; name: string } | null;
  opportunity?: { id: string; name: string; amount: number | null } | null;
  lead?: { id: string; company_name: string } | null;
}

interface RelatedNote {
  id: string;
  content: string;
  note_type: string;
  created_at: string;
  contact?: { first_name: string; last_name: string } | null;
}

type MeetingReportRow = Tables<'crm_meeting_reports'>;

const MEETING_REPORT_STATUSES: readonly MeetingReportStatus[] = [
  'draft',
  'transcript_fetched',
  'cr_generated',
  'actions_validated',
  'email_sent',
  'error',
  'no_meeting',
];

const toMeetingReportStatus = (value: string): MeetingReportStatus =>
  (MEETING_REPORT_STATUSES as readonly string[]).includes(value)
    ? (value as MeetingReportStatus)
    : 'draft';

const isJsonObject = (value: Json): value is { [key: string]: Json | undefined } =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const EXTRACTED_ACTION_TYPES: readonly ExtractedAction['type'][] = ['task', 'reminder', 'appointment'];
const EXTRACTED_ACTION_PRIORITIES: readonly ExtractedAction['priority'][] = ['low', 'medium', 'high', 'critical'];

const asString = (value: Json | undefined): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asNullableString = (value: Json | undefined): string | null | undefined => {
  if (value === null) return null;
  return typeof value === 'string' ? value : undefined;
};

// The `extracted_actions` / `validated_actions` columns are jsonb; the DB type
// is therefore `Json`. The edge function persists ExtractedAction objects, so we
// validate each element's shape at runtime instead of blindly casting.
const toExtractedAction = (value: Json): ExtractedAction | null => {
  if (!isJsonObject(value)) return null;
  const type = value.type;
  const title = value.title;
  const priority = value.priority;
  if (typeof type !== 'string' || !(EXTRACTED_ACTION_TYPES as readonly string[]).includes(type)) return null;
  if (typeof title !== 'string') return null;
  const safePriority: ExtractedAction['priority'] =
    typeof priority === 'string' && (EXTRACTED_ACTION_PRIORITIES as readonly string[]).includes(priority)
      ? (priority as ExtractedAction['priority'])
      : 'medium';
  return {
    type: type as ExtractedAction['type'],
    title,
    description: asString(value.description),
    assignee: asNullableString(value.assignee),
    due_date: asNullableString(value.due_date),
    priority: safePriority,
    created_entity_id: asNullableString(value.created_entity_id),
    created_entity_type: asNullableString(value.created_entity_type),
  };
};

const toExtractedActions = (value: Json | null): ExtractedAction[] =>
  Array.isArray(value)
    ? value.map(toExtractedAction).filter((a): a is ExtractedAction => a !== null)
    : [];

/** Normalizes a raw crm_meeting_reports row (nullable/Json artefacts) into the domain type. */
const toMeetingReport = (row: MeetingReportRow): MeetingReport => ({
  id: row.id,
  appointment_id: row.appointment_id,
  note_id: row.note_id,
  google_meet_conference_id: row.google_meet_conference_id,
  transcript_raw: row.transcript_raw,
  transcript_fetched_at: row.transcript_fetched_at,
  cr_title: row.cr_title,
  cr_html: row.cr_html,
  cr_markdown: row.cr_markdown,
  cr_generated_at: row.cr_generated_at,
  prompt_template_used: row.prompt_template_used,
  extracted_actions: toExtractedActions(row.extracted_actions),
  validated_actions: toExtractedActions(row.validated_actions),
  actions_validated_at: row.actions_validated_at,
  email_sent_to: row.email_sent_to,
  email_sent_at: row.email_sent_at,
  email_resend_id: row.email_resend_id,
  status: toMeetingReportStatus(row.status),
  error_message: row.error_message,
  owner_id: row.owner_id ?? '',
  created_at: row.created_at ?? '',
  updated_at: row.updated_at ?? '',
});

export function MeetingReportSidebar({ reportId, isOpen, onClose }: MeetingReportSidebarProps) {
  const { openAccountSidebar, openContactSidebar, openOpportunitySidebar, openLeadSidebar, openAppointmentSidebar } = useSidebar();
  const [report, setReport] = useState<MeetingReport | null>(null);
  const [appointment, setAppointment] = useState<RelatedAppointment | null>(null);
  const [note, setNote] = useState<RelatedNote | null>(null);
  const [loading, setLoading] = useState(false);
  const [crExpanded, setCrExpanded] = useState(true);
  const [actionsExpanded, setActionsExpanded] = useState(true);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { state: sendState, reload: reloadSendState } = useMeetingReportSendState(isOpen ? reportId : null);

  useEffect(() => {
    if (!reportId || !isOpen) {
      setReport(null);
      setAppointment(null);
      setNote(null);
      return;
    }

    setLoading(true);

    supabase
      .from('crm_meeting_reports')
      .select('*')
      .eq('id', reportId)
      .single()
      .then(async ({ data, error }) => {
        if (error || !data) {
          setLoading(false);
          return;
        }

        setReport(toMeetingReport(data));

        // Fetch related appointment
        if (data.appointment_id) {
          const { data: apt } = await supabase
            .from('appointments')
            .select('id, title, start_time, contact_id, account_id, opportunity_id, onboarding_lead_id, contact:crm_contacts(id, first_name, last_name, email), account:crm_accounts(id, name)')
            .eq('id', data.appointment_id)
            .single();

          const aptData = apt as unknown as RelatedAppointment;

          // Fetch opportunity if linked
          if (aptData?.opportunity_id) {
            const { data: oppData } = await supabase.from('crm_opportunities').select('id, name, amount').eq('id', aptData.opportunity_id).single();
            if (oppData) aptData.opportunity = oppData;
          }
          // Fetch lead if linked
          if (aptData?.onboarding_lead_id) {
            const { data: leadData } = await supabase.from('onboarding_leads').select('id, company_name').eq('id', aptData.onboarding_lead_id).single();
            if (leadData) aptData.lead = leadData;
          }

          setAppointment(aptData);
        }

        // Fetch related note
        if (data.note_id) {
          const { data: nt } = await supabase
            .from('crm_notes')
            .select('id, content, note_type, created_at, contact:crm_contacts(first_name, last_name)')
            .eq('id', data.note_id)
            .single();
          setNote(nt as unknown as RelatedNote);
        }

        setLoading(false);
      });
  }, [reportId, isOpen]);

  if (!reportId) return null;

  const statusConfig = report ? STATUS_CONFIG[report.status] : null;
  const extractedActions: ExtractedAction[] = report?.extracted_actions || [];

  const contactEmail = appointment?.contact?.email;
  const contactName = appointment?.contact
    ? `${appointment.contact.first_name} ${appointment.contact.last_name}`
    : null;
  // Sendable or not, and the subject of the mail: the server's one definition (declined, meeting
  // cancelled or no-show, send in flight… are all refused there too).
  const canSendEmail = Boolean(report && report.cr_html && sendState?.sendable);
  const blockedText = report?.cr_html && !report.email_sent_at && sendState && !sendState.sendable
    ? sendReasonText(sendState.reason)
    : null;

  const handleSendEmail = async (to: string, cc: string[], subject: string, html: string): Promise<{ success: boolean; error?: string }> => {
    if (!report) return { success: false, error: 'Rapport non disponible' };

    const { data, error } = await supabase.functions.invoke('send-cr-email', {
      body: {
        report_id: report.id,
        to_email: to,
        cc,
        to_name: contactName || undefined,
        subject,
        cr_html_override: html !== report.cr_html ? html : undefined,
        expected_cr_generated_at: report.cr_generated_at ?? undefined,
      },
    });
    void reloadSendState();

    if (error) {
      // A refusal (declined, meeting not held, version changed…) is a 409 whose reason is in the body.
      return { success: false, error: await edgeErrorMessage(error, "Erreur lors de l'envoi") };
    }
    if (!data?.success) {
      return { success: false, error: data?.error || "Erreur lors de l'envoi" };
    }

    // Refresh report to update displayed status
    const { data: updated } = await supabase
      .from('crm_meeting_reports')
      .select('*')
      .eq('id', report.id)
      .single();
    if (updated) setReport(toMeetingReport(updated));

    return { success: true };
  };

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/30 z-40"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="absolute right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex-shrink-0 border-b border-neutral-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <FileText className="w-5 h-5 text-violet-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-semibold text-neutral-900 truncate">
                      {report?.cr_title || 'Compte-rendu'}
                    </h2>
                    {statusConfig && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {loading && !report ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                </div>
              ) : report ? (
                <>
                  {/* Metadonnees */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-violet-50 rounded-xl p-3 text-center">
                      <Clock className="w-4 h-4 text-violet-400 mx-auto mb-1" />
                      <p className="text-xs font-medium text-violet-700">
                        {report.cr_generated_at
                          ? format(new Date(report.cr_generated_at), 'dd MMM yyyy HH:mm', { locale: fr })
                          : 'Non genere'}
                      </p>
                      <p className="text-xs text-violet-500 mt-0.5">Genere le</p>
                    </div>
                    <div className="bg-neutral-50 rounded-xl p-3 text-center">
                      {report.email_sent_at ? (
                        <>
                          <MailCheck className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
                          <p className="text-xs font-medium text-neutral-700 truncate">{report.email_sent_to}</p>
                          <p className="text-xs text-neutral-500 mt-0.5">
                            {format(new Date(report.email_sent_at), 'dd/MM HH:mm', { locale: fr })}
                          </p>
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 text-neutral-400 mx-auto mb-1" />
                          <p className="text-xs font-medium text-neutral-500">Non envoye</p>
                          <p className="text-xs text-neutral-400 mt-0.5">Email</p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Contenu du CR */}
                  {report.cr_html && (
                    <div className="bg-neutral-50 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setCrExpanded(!crExpanded)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-100 transition-colors"
                      >
                        <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Compte-rendu
                        </h3>
                        {crExpanded ? (
                          <ChevronUp className="w-4 h-4 text-neutral-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-neutral-400" />
                        )}
                      </button>
                      {crExpanded && <MeetingReportView html={report.cr_html} className="px-4 pb-4" />}
                    </div>
                  )}

                  {/* Actions extraites */}
                  {extractedActions.length > 0 && (
                    <div className="bg-neutral-50 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setActionsExpanded(!actionsExpanded)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-100 transition-colors"
                      >
                        <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Actions detectees ({extractedActions.length})
                        </h3>
                        {actionsExpanded ? (
                          <ChevronUp className="w-4 h-4 text-neutral-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-neutral-400" />
                        )}
                      </button>
                      {actionsExpanded && (
                        <div className="px-4 pb-4 space-y-2">
                          {extractedActions.map((action, idx) => (
                            <div
                              key={idx}
                              className="flex items-start gap-3 p-3 bg-white rounded-lg border border-neutral-200"
                            >
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${EXTRACTED_ACTION_TYPE_COLORS[action.type]}`}>
                                {EXTRACTED_ACTION_TYPE_LABELS[action.type]}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-neutral-900">{action.title}</p>
                                {action.description && (
                                  <p className="text-xs text-neutral-500 mt-0.5" data-sensitive-text="">{action.description}</p>
                                )}
                                <div className="flex items-center gap-3 mt-1">
                                  {action.due_date && (
                                    <span className="text-xs text-neutral-500">
                                      Echeance: {isNaN(new Date(action.due_date).getTime())
                                        ? action.due_date
                                        : format(new Date(action.due_date), 'dd/MM/yyyy')}
                                    </span>
                                  )}
                                  <span className={`text-xs font-medium ${PRIORITY_COLORS[action.priority]}`}>
                                    {PRIORITY_LABELS[action.priority]}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* RDV associe */}
                  {appointment && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">
                        Rendez-vous associe
                      </h3>
                      <button
                        onClick={() => { onClose(); setTimeout(() => openAppointmentSidebar(appointment.id), 100); }}
                        className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-neutral-200 hover:bg-blue-50 hover:border-blue-200 transition-colors w-full text-left"
                      >
                        <div className="p-1.5 bg-blue-50 rounded-lg">
                          <Calendar className="w-4 h-4 text-[#0066FF]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-neutral-900 truncate">{appointment.title}</p>
                          <p className="text-xs text-neutral-500">
                            {format(new Date(appointment.start_time), 'dd MMM yyyy a HH:mm', { locale: fr })}
                          </p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                      </button>

                      {/* Contact */}
                      {appointment.contact && appointment.contact_id && (
                        <button
                          onClick={() => { onClose(); setTimeout(() => openContactSidebar(appointment.contact!.id), 100); }}
                          className="mt-2 flex items-center gap-2 w-full p-2 bg-white rounded-lg border border-neutral-200 hover:bg-blue-50 hover:border-blue-200 transition-colors text-left"
                        >
                          <div className="p-1 bg-blue-100 rounded">
                            <User className="w-3 h-3 text-blue-600" />
                          </div>
                          <span className="text-xs font-medium text-neutral-700 truncate">
                            {appointment.contact.first_name} {appointment.contact.last_name}
                          </span>
                          <ExternalLink className="w-3 h-3 text-neutral-400 ml-auto flex-shrink-0" />
                        </button>
                      )}

                      {/* Compte */}
                      {appointment.account && (
                        <button
                          onClick={() => { onClose(); setTimeout(() => openAccountSidebar(appointment.account!.id), 100); }}
                          className="mt-2 flex items-center gap-2 w-full p-2 bg-white rounded-lg border border-neutral-200 hover:bg-emerald-50 hover:border-emerald-200 transition-colors text-left"
                        >
                          <div className="p-1 bg-emerald-100 rounded">
                            <Building2 className="w-3 h-3 text-emerald-600" />
                          </div>
                          <span className="text-xs font-medium text-neutral-700 truncate">{appointment.account.name}</span>
                          <ExternalLink className="w-3 h-3 text-neutral-400 ml-auto flex-shrink-0" />
                        </button>
                      )}

                      {/* Opportunite */}
                      {appointment.opportunity && (
                        <button
                          onClick={() => { onClose(); setTimeout(() => openOpportunitySidebar(appointment.opportunity!.id), 100); }}
                          className="mt-2 flex items-center gap-2 w-full p-2 bg-white rounded-lg border border-neutral-200 hover:bg-purple-50 hover:border-purple-200 transition-colors text-left"
                        >
                          <div className="p-1 bg-purple-100 rounded">
                            <TrendingUp className="w-3 h-3 text-purple-600" />
                          </div>
                          <span className="text-xs font-medium text-neutral-700 truncate">{appointment.opportunity.name}</span>
                          <ExternalLink className="w-3 h-3 text-neutral-400 ml-auto flex-shrink-0" />
                        </button>
                      )}

                      {/* Lead */}
                      {appointment.lead && (
                        <button
                          onClick={() => { onClose(); setTimeout(() => openLeadSidebar(appointment.lead!.id), 100); }}
                          className="mt-2 flex items-center gap-2 w-full p-2 bg-white rounded-lg border border-neutral-200 hover:bg-amber-50 hover:border-amber-200 transition-colors text-left"
                        >
                          <div className="p-1 bg-amber-100 rounded">
                            <UserCheck className="w-3 h-3 text-amber-600" />
                          </div>
                          <span className="text-xs font-medium text-neutral-700 truncate">{appointment.lead.company_name}</span>
                          <ExternalLink className="w-3 h-3 text-neutral-400 ml-auto flex-shrink-0" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Note associee */}
                  {note && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">
                        Note associee
                      </h3>
                      <div className="flex items-start gap-3 p-2.5 bg-white rounded-lg border border-neutral-200">
                        <div className="p-1.5 bg-amber-100 rounded-lg mt-0.5">
                          <StickyNote className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-neutral-500 mb-1">
                            {note.note_type === 'meeting_note' ? 'Note de reunion' :
                             note.note_type === 'call_note' ? 'Note d\'appel' : 'Note'}
                            {' · '}
                            {formatDistanceToNow(new Date(note.created_at), { addSuffix: true, locale: fr })}
                          </p>
                          <p className="text-sm text-neutral-700 line-clamp-3" data-sensitive-text="">
                            {note.content?.replace(/<[^>]*>/g, '').slice(0, 200) || 'Contenu vide'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Transcript brut */}
                  {report.transcript_raw && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <button
                        onClick={() => setTranscriptExpanded(!transcriptExpanded)}
                        className="w-full flex items-center justify-between"
                      >
                        <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5" /> Transcript
                          <span className="text-neutral-400 normal-case font-normal">
                            ({Math.round(report.transcript_raw.length / 1000)}k car.)
                          </span>
                        </h3>
                        {transcriptExpanded ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
                      </button>
                      {transcriptExpanded && (
                        <pre className="mt-3 text-xs text-neutral-600 whitespace-pre-wrap max-h-80 overflow-y-auto bg-white p-3 rounded-lg border border-neutral-200 font-sans leading-relaxed" data-sensitive-text="">
                          {report.transcript_raw}
                        </pre>
                      )}
                    </div>
                  )}

                  {/* Infos supplementaires */}
                  <div className="bg-neutral-50 rounded-xl p-4 space-y-2">
                    <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Informations</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">Cree</span>
                      <span className="text-sm text-neutral-600">
                        {formatDistanceToNow(new Date(report.created_at), { addSuffix: true, locale: fr })}
                      </span>
                    </div>
                    {report.transcript_fetched_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-neutral-500">Transcript</span>
                        <span className="text-sm text-neutral-600">
                          {format(new Date(report.transcript_fetched_at), 'dd/MM HH:mm', { locale: fr })}
                        </span>
                      </div>
                    )}
                    {report.error_message && (
                      <div className="mt-2 p-2 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-600">{report.error_message}</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p className="text-sm">Compte-rendu introuvable</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-neutral-200 px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {appointment?.account && (
                  <button
                    onClick={() => {
                      onClose();
                      setTimeout(() => openAccountSidebar(appointment.account!.id), 100);
                    }}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Voir le compte
                  </button>
                )}
                {!appointment?.account && (
                  <span className="text-sm text-neutral-400">Aucun compte associe</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {blockedText && <span className="max-w-[16rem] text-xs text-amber-700">{blockedText}</span>}
                {canSendEmail && (
                  <button
                    onClick={() => setShowEmailModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Envoyer par email
                  </button>
                )}
                {report?.cr_generated_at && (
                  <span className="text-xs text-neutral-400">
                    ID: {report.id.slice(0, 8)}...
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>

    {report && canSendEmail && (
      <EmailPreviewModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        initialTo={contactEmail || ''}
        initialCc={sendState?.defaultCc ?? []}
        initialSubject={sendState?.subject ?? 'Compte-rendu'}
        initialHtml={report.cr_html || ''}
      />
    )}
    </>
  );
}
